import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  FileInput,
  HelperText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "flowbite-react";
import Input from "../inputs/Input";
import SelectInput from "../inputs/SelectInput";
import AutoCompleteInput from "../inputs/Autocomplete";
import ComunicateSellSection from "./ComunicateSellSection";
import * as yup from "yup";
import { useEffect, useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import { CVS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";

const schema = yup.object().shape({
  placa: yup
    .string()
    .required("A placa é obrigatória")
    .matches(
      /^([A-Z]{3}[0-9][A-Z0-9][0-9]{2})$/,
      "Formato de placa inválido. Ex.: ABC1234 ou ABC1D23"
    ),
  renavam: yup
    .string()
    .required("O renavam é obrigatório")
    .matches(/^\d{9,11}$/, "O renavam deve conter 9 ou 11 dígitos numéricos"),
  anoFabricacao: yup
    .string()
    .required("O ano de fabricação é obrigatório")
    .matches(/^\d{4}$/, "O ano de fabricação deve ter 4 dígitos"),
  anoModelo: yup
    .string()
    .required("O ano do modelo é obrigatório")
    .matches(/^\d{4}$/, "O ano do modelo deve ter 4 dígitos"),
  tipoCRV: yup.string().required("O tipo do CRV é obrigatório"),
  numeroCRV: yup
    .string()
    .required("O número do CRV é obrigatório")
    .matches(/^\d+$/, "O número do CRV deve conter apenas números"),
  dataEmissaoCRV: yup
    .string()
    .typeError("A data de emissão do CRV deve ser uma data válida")
    .required("A data é obrigatória"),
  ufEmissaoCRV: yup
    .string()
    .required("A UF é obrigatória")
    .matches(/^[A-Z]{2}$/, "A UF deve ser composta por 2 letras maiúsculas"),
  codigoSegurancaCRV: yup.string().required("O código é obrigatório"),
});

const VehicleData = ({
  setActiveStep,
  communicateSell,
  setCommunicateSell,
  states,
  isSubmitting,
}) => {
  const { user } = useApplication();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (communicateSell) {
      Object.keys(communicateSell).forEach((s) => {
        setValue(s, communicateSell[s]);
      });
    }
  }, [communicateSell, setValue]);

  const onSubmit = async (data) => {
    setCommunicateSell(data);
    setActiveStep(2);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ComunicateSellSection name="Leitura automática de ATPV">
        <div className="">
          <div>
            <div className="relative flex flex-col md:flex-row gap-2">
              <div className="relative flex-1">
                <FileInput
                  register={register}
                  accept=".jpg,.jpeg,.png"
                  onChange={async (e) => {
                    setIsUploading(true);
                    const file = e.target.files[0];
                    if (!file) return;

                    const formData = new FormData();
                    formData.append("file", file);

                    try {
                      const res = await axiosInstance.post(
                        CVS.UPLOAD_ATPV,
                        formData,
                        {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );

                      const result = res.data;

                      if (result?.data) {
                        Object.entries(result.data).forEach(([key, value]) => {
                          setValue(key, value);
                        });
                        setValue("tipoCRV", 1);
                        setValue("numeroViaCRV", 1);
                        setValue(
                          "codigoTipoPessoaVendedor",
                          result.data?.cpfCnpjVendedor?.length === 14 ? 1 : 0
                        );
                        setValue(
                          "codigoTipoPessoaComprador",
                          result.data?.cpfCnpjComprador?.length === 14 ? 1 : 0
                        );
                      }
                    } catch (err) {
                      console.error("Erro ao enviar arquivo:", err);
                    }
                    setIsUploading(false);
                  }}
                />
                {isUploading && (
                  <div className="absolute right-3 top-2">
                    <Spinner color="success" />
                  </div>
                )}
              </div>
              <Button className="primary" onClick={() => setOpenModal(true)}>
                Visualizar modelo
              </Button>
            </div>
            <small className="text-gray-700">
              * Arquivo esperado: PNG ou JPG. Obs: Para uma maior precisão no
              preenchimento automático das informações, tente enviar um
              documento com alta qualidade e boa legibilidade.
            </small>
          </div>
        </div>
      </ComunicateSellSection>
      <ComunicateSellSection name="Dados do veículo">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              register={register}
              name="placa"
              label="Placa *"
              required={true}
              maxLength={7}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
            {errors.placa?.message && (
              <small className="text-red-600">{errors.placa?.message}</small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="renavam"
              label="Renavam *"
              required={true}
              maxLength="11"
              uppercase
            />
            {errors.renavam?.message && (
              <small className="text-red-600">{errors.renavam?.message}</small>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          <div>
            <Input
              register={register}
              name="anoFabricacao"
              label="Ano Fabricação *"
              maxLength="4"
              required={true}
            />
            {errors.anoFabricacao?.message && (
              <small className="text-red-600">
                {errors.anoFabricacao?.message}
              </small>
            )}
          </div>
          <div>
            <Input
              register={register}
              name="anoModelo"
              label="Ano Modelo *"
              maxLength="4"
              required={true}
            />
            {errors.anoModelo?.message && (
              <small className="text-red-600">
                {errors.anoModelo?.message}
              </small>
            )}
          </div>
          <div>
            <SelectInput
              register={register}
              name="tipoCRV"
              label="Tipo do CRV *"
              required={true}
            >
              <option value="">Selecione</option>
              <option value={1}>Branco</option>
              <option value={2}>Verde</option>
            </SelectInput>
            {errors.tipoCRV?.message && (
              <small className="text-red-600">{errors.tipoCRV?.message}</small>
            )}
          </div>

          <div>
            <SelectInput
              register={register}
              name="numeroViaCRV"
              label="Número Via CRV *"
              required={true}
            >
              <option value="">Selecione</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </SelectInput>
            {errors.numeroViaCRV?.message && (
              <small className="text-red-600">
                {errors.numeroViaCRV?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="numeroCRV"
              label="Número CRV *"
              required={true}
              uppercase
            />
            {errors.numeroCRV?.message && (
              <small className="text-red-600">
                {errors.numeroCRV?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="dataEmissaoCRV"
              label="Data emissão CRV *"
              type="date"
              required={true}
            />
            {errors.dataEmissaoCRV?.message && (
              <small className="text-red-600">
                {errors.dataEmissaoCRV?.message}
              </small>
            )}
          </div>

          <div>
            <Input
              register={register}
              name="codigoSegurancaCRV"
              label="Cód. segurança CRV *"
              required={true}
              maxLength="13"
              uppercase
            />
            {errors.codigoSegurancaCRV?.message && (
              <small className="text-red-600">
                {errors.codigoSegurancaCRV?.message}
              </small>
            )}
          </div>

          <div>
            <AutoCompleteInput
              register={register}
              name="ufEmissaoCRV"
              label="UF Emissão CRV *"
              options={states}
              value={watch("ufEmissaoCRV")}
              onChange={(user) => setValue("ufEmissaoCRV", user.value)}
              required={true}
            />
            {errors.ufEmissaoCRV?.message && (
              <small className="text-red-600">
                {errors.ufEmissaoCRV?.message}
              </small>
            )}
          </div>
          {user?.companyId == 1 && communicateSell?.status == 1 && (
            <div>
              <Input
                register={register}
                name="assinaturaEletronica"
                label="Assinatura Eletrônica"
              />{" "}
            </div>
          )}
        </div>
      </ComunicateSellSection>

      <div className="flex justify-center items-center gap-2 mt-10">
        <Button
          type="submit"
          className="primary"
          disabled={isSubmitting}
          isProcessing={isSubmitting}
        >
          {!isSubmitting ? "Próximo" : "Salvando..."}
        </Button>
      </div>
      {error && (
        <Alert color="failure" withBorderAccent className="mt-4">
          Erro ao tentar salvar o comunicado de venda! Tente novamente mais
          tarde.
        </Alert>
      )}

      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <ModalHeader>Modelo de ATPV</ModalHeader>
        <ModalBody className="bg-gray-100">
          <img
            className="max-w-80 mx-auto shadow-md rounded-md"
            src="/assets/communicate-sell/model-atpv.jpg"
          />
        </ModalBody>
        <ModalFooter className="flex justify-center">
          <Button className="bg-gray-500" onClick={() => setOpenModal(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

export default VehicleData;
